import {
    TenantAffiliateResponseModel,
    UserDataConsentModelNameEnum,
} from '@swagger-http';

import { CustomerSegment, InverterProviders, SurveyType } from '@store/enums';
import {
    useAppSelector,
    useEnergyProvider,
    useHasBattery,
    useHasElectricCar,
    useHasGcp,
    useHasInverter,
    useHasWallbox,
} from '@store/selectors';

export const useSite = () => useAppSelector((state) => state.user.site);

export const useSiteTimezone = () =>
    useAppSelector((state) => state.user.site.timezone) || 'Europe/London';

export const useUserSource = () => useAppSelector((state) => state.user.source);

export const useRegistrationDate = () => {
    const regDate = useAppSelector((state) => state.user.registrationDate);
    const joinDate = useAppSelector((state) => state.user.joinDate);

    const hasGCP = useHasGcp();
    const hasBattery = useHasBattery();
    const hasInverter = useHasInverter();

    return !hasGCP && !hasBattery && !hasInverter ? joinDate : regDate;
};

export const useHasSales = () => useAppSelector((state) => state.user.hasSales);

export const getCurrentTenantData = <T extends TenantAffiliateResponseModel>(
    tenants: T[],
    tenantId?: string,
): T | undefined => tenants.find((tenant: T) => tenant?.tenantId === tenantId);

export const getTenantItem = <
    T extends TenantAffiliateResponseModel,
    K extends keyof T,
>(
    tenant: T,
    tenants: T[],
    key: K,
    id?: string,
) => {
    if (!!tenant && Object.keys(tenant).length > 0) {
        return tenant[key];
    }

    const config = getCurrentTenantData(tenants, id);

    if (!config) {
        return;
    }

    return config[key];
};

export const useTenantData = <T extends keyof TenantAffiliateResponseModel>(
    field: T,
): TenantAffiliateResponseModel[T] => {
    const tenantId = useAppSelector((state) => state.user.tenantId);
    const { config, details } = useAppSelector(
        (state) => state.settings.tenants,
    );

    return getTenantItem(config, details, field, tenantId);
};

// prettier-ignore
export const useCompletedCMSSurveys = () => useAppSelector((state) => state.user.surveys[SurveyType.CMS].completed);

// prettier-ignore
export const useLoginCount = (): number => useAppSelector((state) => state.user.loginCount);

export const useCustomerSegments = (): CustomerSegment[] => {
    const provider = useEnergyProvider();
    const hasWallbox = useHasWallbox();
    const hasElectricCar = useHasElectricCar();

    if (!provider && !hasWallbox && !hasElectricCar) {
        return [CustomerSegment.NO_DEVICES];
    }

    const result = [];

    if (provider) {
        result.push(
            provider === InverterProviders.UKMeter
                ? CustomerSegment.SMART_METER
                : CustomerSegment.SOLAR,
        );
    }

    if (hasWallbox || hasElectricCar) {
        result.push(CustomerSegment.EMOBILITY);
    }

    return result;
};

// prettier-ignore
export const useBillingRegion = () => useAppSelector(state => state.user.billingRegion)

// prettier-ignore
export const useBillingRegions = () => useAppSelector(state => state.user.billingRegions)

export const useShouldShowRegionSelector = (): boolean => {
    const { data } = useBillingRegions();

    if (data && data.length <= 1) {
        return false;
    }

    return true;
};

export const useGetDataAnalysisConsent = () =>
    useAppSelector((data) => data.user.consents).find(
        (item) => item.name === UserDataConsentModelNameEnum.DataAnalysis,
    );
